import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Grid,
    Button,
    Alert,
    Snackbar,
    FormControl,
    Input,
    InputLabel,
    FormHelperText,
    Autocomplete,
    TextField,
} from '@mui/material';
import { Popper } from '@mui/base/Popper';
import { UserOrganizationRole } from './userFormControl/UserOrganizationRole';
import { useUserFields } from './hooks/useUserFields';

export const UserFields = ({ pageTitle, cashRegistersList, userInfo }) => {
    const { id: userId = '' } = userInfo || {};
    const { t } = useTranslation();

    const {
        user,
        roles,
        userRoles,
        logins,
        existingAccess,
        existingCashRegisters,
        deletionIndex,
        open,
        anchorEl,
        idPopup,
        readyToSave,
        openSuccess,
        error,
        alertText,
        removeCashRegisters,
        setAnchorEl,
        handleClose,
        handleOpenPopup,
        handleChangeUser,
        handleLoginInput,
        handleChangeAccess,
        handleChangeCashRegister,
        handleCashRegisterAccess,
        addNewOrganizationRole,
        handleAccessManage,
        saveUser,
        editUser,
    } = useUserFields(userInfo);

    return (
        <Box
            sx={{ flexGrow: 1, marginTop: 3, marginLeft: 10, marginBottom: 10 }}
        >
            <Grid container sx={{ marginTop: 2 }} spacing={4} align='left'>
                <Grid item xs={6}>
                    <Typography
                        sx={{ fontSize: 24 }}
                        color='pimary'
                        gutterBottom
                    >
                        {t(pageTitle)}
                    </Typography>
                </Grid>
                {!userInfo && (
                    <Grid item xs={5} align='right'>
                        <Button
                            variant='outlined'
                            disabled={!readyToSave}
                            onClick={() => saveUser()}
                        >
                            {t('save_user_btn')}
                        </Button>
                    </Grid>
                )}
            </Grid>
            <Grid container item xs={12}>
                <Grid
                    container
                    item
                    sx={{ marginTop: 2 }}
                    spacing={4}
                    align='left'
                >
                    <Grid item xs={userInfo ? 4 : 5}>
                        <FormControl fullWidth sx={{ m: 1 }} variant='standard'>
                            <Autocomplete
                                id='component-autocomplete'
                                options={logins}
                                getOptionLabel={login => {
                                    return login &&
                                        Object.prototype.hasOwnProperty.call(
                                            login,
                                            'login',
                                        )
                                        ? // login.hasOwnProperty('login')
                                          login.login
                                        : login;
                                }}
                                freeSolo
                                value={user.simpalsLogin}
                                disabled={!!userInfo}
                                onChange={(event, newValue) => {
                                    handleChangeUser({
                                        simpalsLogin: newValue?.login || '',
                                        simpalsId: newValue?.userId || '',
                                    });
                                }}
                                onInputChange={handleLoginInput}
                                renderInput={params => {
                                    return (
                                        <TextField
                                            {...params}
                                            label={t('simpals_login')}
                                            variant='standard'
                                            value={user.simpalsLogin}
                                        />
                                    );
                                }}
                            />
                            <FormHelperText id='standard-helper-text'>
                                {t('must_not_empty')}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={userInfo ? 4 : 5}>
                        <FormControl fullWidth sx={{ m: 1 }} variant='standard'>
                            <InputLabel htmlFor='component-simple'>
                                {t('surname_first_name')}
                            </InputLabel>
                            <Input
                                id='component-simple'
                                value={user.name}
                                onChange={event => {
                                    handleChangeUser({
                                        name: event.target.value,
                                    });
                                }}
                                name='name'
                                aria-describedby='standard-helper-text'
                                inputProps={{
                                    'aria-label': 'text',
                                }}
                            />
                            <FormHelperText id='standard-helper-text'>
                                {t('must_not_empty')}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    {userInfo && (
                        <Grid
                            item
                            xs={3}
                            justifyContent='left'
                            container
                            alignItems='center'
                        >
                            <Grid item xs={12} align='center'>
                                <Button
                                    variant='outlined'
                                    disabled={
                                        !readyToSave ||
                                        userInfo.name.trim() ===
                                            user.name.trim()
                                    }
                                    onClick={() => editUser()}
                                >
                                    {t('save_name')}
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
                {userRoles.map((access, index) => {
                    return (
                        <UserOrganizationRole
                            key={index}
                            index={index}
                            access={access}
                            roles={roles}
                            cashRegistersSet={user.cashRegisters}
                            cashRegistersList={cashRegistersList}
                            setAccess={handleChangeAccess}
                            setUserCashRegister={handleChangeCashRegister}
                            allowRemoving={userRoles.length > 1}
                            editUser={!!userInfo}
                            existingAccess={
                                existingAccess?.includes(access.id) || false
                            }
                            existingCashRegisters={existingCashRegisters}
                            handleAccessManage={handleAccessManage}
                            handleCashRegisterAccess={handleCashRegisterAccess}
                            handleOpenPopup={handleOpenPopup}
                            pageTitle={pageTitle}
                            userId={userId}
                            userRoles={userRoles}
                        />
                    );
                })}
                <Grid container item sx={{ marginTop: 2 }} align='left'>
                    <Button variant='outlined' onClick={addNewOrganizationRole}>
                        {t('add_new_role')}
                    </Button>
                </Grid>
                <Grid container item>
                    <Popper id={idPopup} open={open} anchorEl={anchorEl}>
                        <Box
                            component='div'
                            sx={{
                                display: 'block',
                                backgroundColor: 'white',
                                boxShadow: '0px 4px 8px rgb(0 0 0 / 0.1)',
                                borderRadius: '8px',
                                padding: '20px',
                                border: '1px solid rgb(0 0 0 / 0.2)',
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                variant='body1'
                                component='div'
                                sx={{ marginBottom: 1 }}
                            >
                                {t('are_you_sure')}
                            </Typography>
                            <Button
                                variant='outlined'
                                sx={{ marginRight: 1 }}
                                onClick={() => {
                                    handleAccessManage(deletionIndex, false);
                                    if (removeCashRegisters) {
                                        handleCashRegisterAccess(false);
                                    }
                                }}
                            >
                                {t('yes')}
                            </Button>
                            <Button
                                variant='outlined'
                                onClick={() => setAnchorEl(null)}
                            >
                                {t('no')}
                            </Button>
                        </Box>
                    </Popper>
                </Grid>
            </Grid>
            <Snackbar
                open={openSuccess}
                autoHideDuration={6000}
                onClose={handleClose}
            >
                <Alert
                    onClose={handleClose}
                    severity='success'
                    sx={{ width: '100%' }}
                >
                    {alertText}
                </Alert>
            </Snackbar>
            <Snackbar
                open={error.status}
                autoHideDuration={6000}
                onClose={handleClose}
            >
                <Alert
                    onClose={handleClose}
                    severity='error'
                    sx={{ width: '100%' }}
                >
                    {error.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};
